import { ref } from 'vue';
import { useResourceLoader } from './useResourceLoader';

// 创建全局单例
const audioContext = ref<AudioContext | null>(null);
const bgmSource = ref<AudioBufferSourceNode | null>(null);
const bgmGain = ref<GainNode | null>(null);
const audioBuffers = ref<Record<string, AudioBuffer>>({});

export const useSound = () => {
  const resourceLoader = useResourceLoader();

  const initSound = async () => {
    try {
      // 创建 AudioContext
      audioContext.value = new AudioContext();
      bgmGain.value = audioContext.value.createGain();
      bgmGain.value.connect(audioContext.value.destination);
      bgmGain.value.gain.value = 0.6;

      // 预加载音频文件
      await resourceLoader.preload([
        '/audio/bgm.mp3',
        '/audio/button_click.mp3',
        '/audio/result_appear_3s.mp3',
        '/audio/result_flip.mp3'
      ]);

      // 加载所有音频buffer
      const audioFiles = ['bgm', 'button_click', 'result_appear_3s', 'result_flip'];

      for (const file of audioFiles) {
        const url = await resourceLoader.getCachedUrl(`/audio/${file}.mp3`);
        if (url) {
          const response = await fetch(url);
          const arrayBuffer = await response.arrayBuffer();
          audioBuffers.value[file] = await audioContext.value.decodeAudioData(arrayBuffer);
        }
      }
    } catch (error) {
      console.error('Failed to initialize sound:', error);
    }
  };

  const playSound = async (
    bufferName: string,
    loop = false,
    gain = 0.8
  ): Promise<AudioBufferSourceNode | null> => {
    if (!audioContext.value || !audioBuffers.value[bufferName]) return null;

    // 确保 AudioContext 处于运行状态
    if (audioContext.value.state === 'suspended') {
      await audioContext.value.resume();
    }

    const source = audioContext.value.createBufferSource();
    const gainNode = audioContext.value.createGain();

    source.buffer = audioBuffers.value[bufferName];
    source.loop = loop;

    gainNode.gain.value = gain;
    source.connect(gainNode);
    gainNode.connect(audioContext.value.destination);

    source.start();
    return source;
  };

  const playBgm = async () => {
    if (bgmSource.value) return;
    const source = await playSound('bgm', true, 0.6);
    if (source) {
      bgmSource.value = source;
    }
  };

  const pauseBgm = () => {
    bgmSource.value?.stop();
    bgmSource.value = null;
  };

  const playButtonClick = async () => {
    await playBgm();
    await playSound('button_click');
  };

  const playResultAppear = async () => {
    await playSound('result_appear_3s');
  };

  const playResultFlip = async () => {
    await playBgm();
    await playSound('result_flip');
  };

  return {
    initSound,
    playBgm,
    pauseBgm,
    playButtonClick,
    playResultAppear,
    playResultFlip
  };
};
